import { useMemo } from "react";
import { Play, Star } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FlowButton } from "@/components/system";
import { trpc } from "@/lib/trpc";
import { buildFlowGroups } from "@/lib/homey-subviews";
import type { HomeySnapshot } from "@/types/homey";
import { useFlowActions } from "./useFlowActions";

interface FlowsViewProps {
  snapshot: HomeySnapshot;
}

/**
 * Flows sub-view: favorites group first, then flows grouped by folder. Each
 * flow is a FlowButton (drives its own running/success/error feedback off the
 * trigger promise) with a star toggle to add/remove it from favorites.
 *
 * To test: open the dashboard, switch to the Flows tab. Flows appear grouped;
 * triggering one shows running -> success on the button; starring a flow moves
 * it into the Favorites group at the top.
 */
export function FlowsView({ snapshot }: FlowsViewProps) {
  const settingsQuery = trpc.homey.getDashboardSettings.useQuery();
  const { trigger, toggleFavorite } = useFlowActions();

  const favoriteIds = useMemo(
    () => settingsQuery.data?.favorite_flow_ids ?? [],
    [settingsQuery.data],
  );

  const groups = useMemo(() => buildFlowGroups(snapshot, favoriteIds), [snapshot, favoriteIds]);

  const favoriteSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);

  if (settingsQuery.isLoading) {
    return (
      <div className="space-y-8">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-5 w-32" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, j) => (
                <Skeleton key={j} className="h-16 rounded-2xl" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <Play className="size-8 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">No flows available to trigger.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {groups.map((group) => (
        <section key={group.name} className="flex flex-col gap-3">
          <h2 className="text-base font-semibold">{group.name}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {group.flows.map((flow) => {
              const isFavorite = favoriteSet.has(flow.id);
              return (
                <div key={flow.id} className="flex items-stretch gap-2">
                  <FlowButton
                    name={flow.name}
                    icon={<Play />}
                    onTrigger={() => trigger(flow)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-pressed={isFavorite}
                    aria-label={
                      isFavorite
                        ? `Remove ${flow.name} from favorites`
                        : `Add ${flow.name} to favorites`
                    }
                    onClick={() => toggleFavorite(flow.id)}
                    className="shrink-0 self-center"
                  >
                    <Star
                      className={cn("size-4", isFavorite && "fill-current text-domain-light")}
                    />
                  </Button>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export default FlowsView;
